/**
 * Pump.fun Profile Library
 * Fetches wallet profile information and generates avatars for traders
 * Similar to how Axiom shows trader profiles on charts
 */

// Removed Solscan dependency to avoid API authentication issues
// Profiles will be generated with basic info and avatars

export interface PumpFunProfile {
  address: string;
  username: string | null;
  avatar: string;
  solBalance: number;
  isWhale: boolean;
  tokenCount: number;
  winRate?: number;
  totalTrades?: number;
  lastActive?: number;
}

const profileCache = new Map<string, { data: PumpFunProfile; ts: number }>();
const CACHE_TTL = 5 * 60_000; // 5 minutes
const inflightProfiles = new Map<string, Promise<PumpFunProfile>>();

/**
 * Generate a consistent avatar URL based on wallet address
 * Uses dicebear API for consistent avatars per address
 */
function generateAvatarUrl(address: string): string {
  const seed = address.slice(0, 16); // Use first 16 chars as seed
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
}

/**
 * Generate a username from wallet address
 * Uses first 4 and last 4 chars with a fun suffix
 */
function generateUsername(address: string): string {
  const prefix = address.slice(0, 4);
  const suffix = address.slice(-4);
  const suffixes = ["_sol", ".deg", ".trader", ".whale", ".ape", ".chad"];
  const randomSuffix = suffixes[address.charCodeAt(5) % suffixes.length];
  return `${prefix}...${suffix}${randomSuffix}`;
}

/**
 * Fetch pump.fun profile for a wallet address
 * Generates profile without external API calls to avoid authentication issues
 */
export async function fetchPumpFunProfile(address: string): Promise<PumpFunProfile> {
  const cached = profileCache.get(address);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.data;
  }

  // Deduplicate concurrent inflight requests
  const inflight = inflightProfiles.get(address);
  if (inflight) return inflight;

  const promise = (async () => {
    let profile: PumpFunProfile | null = null;
    try {
      const res = await fetch(`/pump-frontend/users/${address}`);
      if (res.ok) {
        const data = await res.json();
        if (data) {
          profile = {
            address,
            username: data.username || generateUsername(address),
            avatar: data.profileImageUri || generateAvatarUrl(address),
            solBalance: 0, // Not provided by this endpoint
            isWhale: false,
            tokenCount: 0,
            lastActive: Date.now(),
          };
        }
      }
    } catch (err) {
      // fallback on error
    }

    if (!profile) {
      // Generate basic profile fallback without external API calls
      profile = {
        address,
        username: generateUsername(address),
        avatar: generateAvatarUrl(address),
        solBalance: 0,
        isWhale: false,
        tokenCount: 0,
        winRate: undefined,
        totalTrades: undefined,
        lastActive: Date.now(),
      };
    }

    profileCache.set(address, { data: profile, ts: Date.now() });
    return profile;
  })();

  inflightProfiles.set(address, promise);

  try {
    return await promise;
  } finally {
    inflightProfiles.delete(address);
  }
}

/**
 * Batch fetch profiles for multiple wallet addresses
 * Useful for showing multiple traders on a chart
 */
export async function batchFetchPumpFunProfiles(addresses: string[]): Promise<Map<string, PumpFunProfile>> {
  const profiles = new Map<string, PumpFunProfile>();
  
  await Promise.all(
    addresses.map(async (address) => {
      const profile = await fetchPumpFunProfile(address);
      profiles.set(address, profile);
    })
  );
  
  return profiles;
}

/**
 * Clear profile cache (useful for testing or manual refresh)
 */
export function clearPumpFunProfileCache(): void {
  profileCache.clear();
}
